// Admin Team Management Page
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import GlassCard from '../../components/admin/GlassCard';
import DataTable from '../../components/admin/DataTable';
import LoadingSpinner from '../../components/admin/LoadingSpinner';

const AdminTeam: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    teamCategory: 'core',
    displayOrder: '1',
    bio: '',
    image_url: '',
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: ''
  });
  const imageFileRef = React.useRef<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string>('');
  const [uploading, setUploading] = React.useState(false);

  const getOrderPositionValue = (category: string, displayOrder: string) => {
    const parsed = parseInt(displayOrder, 10) || 1;
    return category === 'supporter' ? 1000 + parsed : parsed;
  };

  const selectedOrderPosition = getOrderPositionValue(formData.teamCategory, formData.displayOrder);
  const isOrderTaken = teamMembers.some(
    (member) => member.id !== editingMember?.id && Number(member.order_position || 0) === selectedOrderPosition
  );

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/team');
      const data = await response.json();
      console.log('Team members data:', data);
      setTeamMembers(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching team members:', error);
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      imageFileRef.current = file;
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      imageFileRef.current = null;
      setImagePreview('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isOrderTaken) {
      alert(`Order ${formData.displayOrder} is already taken in ${formData.teamCategory === 'supporter' ? 'Our Supporters' : 'Core Team Members'}. Please choose another order.`);
      return;
    }
    try {
      setUploading(true);
      let imageUrl = formData.image_url;
      const selectedImageFile = imageFileRef.current;

      // Upload image if a new file is selected
      if (selectedImageFile) {
        console.log('Uploading image:', selectedImageFile.name, 'Type:', selectedImageFile.type, 'Size:', selectedImageFile.size);
        const uploadFormData = new FormData();
        uploadFormData.append('image', selectedImageFile);
        uploadFormData.append('category', 'team');

        try {
          const uploadResponse = await fetch('http://localhost:5000/api/upload', {
            method: 'POST',
            body: uploadFormData
          });

          console.log('Upload response status:', uploadResponse.status, uploadResponse.statusText);

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json().catch(() => ({ error: 'Unknown server error' }));
            console.error('Upload failed:', errorData);
            throw new Error(errorData.error || 'Image upload failed');
          }

          const uploadResult = await uploadResponse.json();
          console.log('Upload successful:', uploadResult);
          imageUrl = uploadResult.url;
        } catch (uploadError) {
          console.error('Network or upload error:', uploadError);
          throw new Error(`Upload failed: ${uploadError instanceof Error ? uploadError.message : 'Network error'}`);
        }
      }

      const url = editingMember
        ? `http://localhost:5000/api/team/${editingMember.id}`
        : 'http://localhost:5000/api/team';
      
      const teamData = {
        name: formData.name,
        role: formData.position,  // Backend expects 'role'
        bio: formData.bio,
        image: imageUrl || '',  // Backend expects 'image'
        facebook: formData.facebook || null,
        twitter: formData.twitter || null,
        linkedin: formData.linkedin || null,
        instagram: formData.instagram || null,
        order_position: selectedOrderPosition,
        active: true
      };
      
      console.log('Sending team data:', teamData);
      
      const response = await fetch(url, {
        method: editingMember ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(teamData)
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        throw new Error(errorData.error || 'Failed to save team member');
      }

      if (response.ok) {
        fetchTeamMembers();
        setShowAddForm(false);
        setEditingMember(null);
        imageFileRef.current = null;
        setImagePreview('');
        setFormData({
          name: '',
          position: '',
          teamCategory: 'core',
          displayOrder: '1',
          bio: '',
          image_url: '',
          facebook: '',
          twitter: '',
          instagram: '',
          linkedin: ''
        });
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Failed to save team member. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (member: any) => {
    const isSupporter = Number(member.order_position || 0) >= 1000;
    setEditingMember(member);
    setFormData({
      name: member.name,
      position: member.role,  // Backend returns 'role'
      teamCategory: isSupporter ? 'supporter' : 'core',
      displayOrder: String(isSupporter ? Number(member.order_position || 1000) - 1000 : Number(member.order_position || 1)),
      bio: member.bio,
      image_url: member.image,  // Backend returns 'image'
      facebook: member.facebook || '',
      twitter: member.twitter || '',
      instagram: member.instagram || '',
      linkedin: member.linkedin || ''
    });
    setImagePreview(member.image ? `http://localhost:5000${member.image}` : '');
    imageFileRef.current = null;
    setShowAddForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await fetch(`http://localhost:5000/api/team/${id}`, {
          method: 'DELETE'
        });
        fetchTeamMembers();
      } catch (error) {
        console.error('Error deleting team member:', error);
      }
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    {
      key: 'category',
      label: 'Category',
      render: (_: any, row: any) => Number(row.order_position || 0) >= 1000 ? 'Our Supporters' : 'Core Team'
    },
    {
      key: 'order_position',
      label: 'Display Order',
      sortable: true,
      render: (value: number) => {
        const n = Number(value || 0);
        return n >= 1000 ? n - 1000 : n;
      }
    },
    { key: 'role', label: 'Position', sortable: true },
    {
      key: 'bio',
      label: 'Bio',
      render: (value: string) => value.substring(0, 40) + '...'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ color: '#111827', fontSize: '26px', fontWeight: '700', marginBottom: '6px', letterSpacing: '-0.02em' }}>
            Team Management
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px', fontWeight: '500' }}>
            Manage team members and their profiles
          </p>
        </div>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingMember(null);
            imageFileRef.current = null;
            setImagePreview('');
            setFormData({
              name: '',
              position: '',
              teamCategory: 'core',
              displayOrder: '1',
              bio: '',
              image_url: '',
              facebook: '',
              twitter: '',
              instagram: '',
              linkedin: ''
            });
          }}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: '#111827',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
        >
          {showAddForm ? '✕ Cancel' : '+ Add Team Member'}
        </button>
      </div>

      {showAddForm && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '16px', letterSpacing: '-0.01em' }}>
            {editingMember ? 'Edit Team Member' : 'Add New Team Member'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Position
                </label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Team Category
                </label>
                <select
                  value={formData.teamCategory}
                  onChange={(e) => setFormData({ ...formData, teamCategory: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                >
                  <option value="core">Core Team Members</option>
                  <option value="supporter">Our Supporters</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Display Order
                </label>
                <input
                  type="number"
                  min={1}
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
                <small style={{ color: '#6B7280', fontSize: '12px' }}>
                  1 appears first in selected category.
                </small>
                {isOrderTaken && (
                  <div style={{ color: '#DC2626', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>
                    Order {formData.displayOrder} is already taken.
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                Profile Image (370 x 451px)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#111827',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              {imagePreview && (
                <div style={{ marginTop: '12px' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '185px',
                      height: '225.5px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid #E5E7EB'
                    }}
                  />
                </div>
              )}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                Bio
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                required
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#111827',
                  outline: 'none',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
                Social Media Links (Optional)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <input
                  type="text"
                  value={formData.facebook}
                  onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                  placeholder="Facebook URL"
                  style={{
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
                <input
                  type="text"
                  value={formData.twitter}
                  onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                  placeholder="Twitter URL"
                  style={{
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
                <input
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  placeholder="Instagram URL"
                  style={{
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
                <input
                  type="text"
                  value={formData.linkedin}
                  onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                  placeholder="LinkedIn URL"
                  style={{
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            <button
              type="submit"
                disabled={uploading || isOrderTaken}
              style={{
                padding: '10px 24px',
                borderRadius: '8px',
                border: 'none',
                background: (uploading || isOrderTaken) ? '#9CA3AF' : '#111827',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: (uploading || isOrderTaken) ? 'not-allowed' : 'pointer',
                opacity: (uploading || isOrderTaken) ? 0.7 : 1
              }}
            >
              {uploading ? 'Uploading...' : (editingMember ? 'Update Member' : 'Add Member')}
            </button>
          </form>
        </GlassCard>
      )}

      <DataTable
        columns={columns}
        data={teamMembers}
        actions={(row) => (
          <>
            <button
              onClick={() => handleEdit(row)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #DBEAFE',
                background: '#EFF6FF',
                color: '#2563EB',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(row.id)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #FEE2E2',
                background: '#FEF2F2',
                color: '#DC2626',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Delete
            </button>
          </>
        )}
      />
    </AdminLayout>
  );
};

export default AdminTeam;
